// Унифицированный хук для Notifications согласно STYLEGUIDE.md
import { useMemo } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { supabaseWorker } from '@/integrations/supabase/workerClient';
import { useAuth } from '@/context/AuthContext';
import { TABLE_NAMES } from '@/constants/tableNames';
import type { 
  Notification, 
  DbNotification, 
  NotificationCreate, 
  NotificationUpdate, 
  NotificationFilters 
} from '@/types/entities/notification';
import { 
  dbNotificationToNotification, 
  notificationCreateToDb, 
  notificationUpdateToDb 
} from '@/utils/mappers/notificationMappers';

export interface UseNotificationsUnifiedReturn {
  notifications: Notification[];
  loading: boolean;
  error: Error | null;
  unreadCount: number;
  createNotification: (data: NotificationCreate) => Promise<Notification>;
  updateNotification: (id: string, data: NotificationUpdate) => Promise<Notification>;
  deleteNotification: (id: string) => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  refetch: () => Promise<void>;
}

export const useNotificationsUnified = (filters?: NotificationFilters): UseNotificationsUnifiedReturn => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  // Построение базового запроса (без сложных условий непрочитанного)
  const baseQuery = useMemo(() => {
    let query = supabaseWorker
      .from(TABLE_NAMES.NOTIFICATIONS)
      .select('*')
      .order('created_at', { ascending: false });

    if (user) {
      query = query.eq('user_id', user.id);
    }
    if (filters?.type) {
      query = query.eq('type', filters.type);
    }
    if (filters?.priority) {
      query = query.eq('priority', filters.priority);
    }
    if (filters?.entityType) {
      query = query.eq('entity_type', filters.entityType);
    }
    if (filters?.limit) {
      query = query.limit(filters.limit);
    }

    return query;
  }, [filters?.type, filters?.priority, filters?.entityType, filters?.limit, user]);

  // Запрос списка уведомлений
  const { 
    data: dbNotifications = [], 
    isLoading: loading, 
    error, 
    refetch 
  } = useQuery({
    queryKey: [TABLE_NAMES.NOTIFICATIONS, user?.id, filters],
    queryFn: async () => {
      // Специальная обработка непрочитанных: read=false ИЛИ read IS NULL
      if (user && filters?.read === false) {
        // ВАЖНО: нельзя переиспользовать один и тот же builder, он мутабелен.
        const qFalse = supabaseWorker
          .from(TABLE_NAMES.NOTIFICATIONS)
          .select('*')
          .order('created_at', { ascending: false })
          .eq('user_id', user.id)
          .eq('read', false);

        const qNull = supabaseWorker
          .from(TABLE_NAMES.NOTIFICATIONS)
          .select('*')
          .order('created_at', { ascending: false })
          .eq('user_id', user.id)
          .is('read', null);

        const [{ data: dFalse, error: eFalse }, { data: dNull, error: eNull }] = await Promise.all([
          qFalse,
          qNull,
        ]);
        if (eFalse) throw eFalse;
        if (eNull) throw eNull;
        const arrFalse: DbNotification[] = (dFalse ?? []) as DbNotification[];
        const arrNull: DbNotification[] = (dNull ?? []) as DbNotification[];
        const merged: DbNotification[] = [...arrFalse, ...arrNull];
        // Удаляем возможные дубликаты по id и сортируем по created_at desc
        const unique: DbNotification[] = Array.from(new Map(merged.map((n: DbNotification) => [n.id, n])).values());
        unique.sort((a: DbNotification, b: DbNotification) => (a.created_at > b.created_at ? -1 : 1));
        return unique;
      }

      const { data, error } = await baseQuery;
      if (error) throw error;
      // Прочитанные-true или отсутствие фильтра по read → уже покрыто baseQuery (eq('read', true) не применяется здесь)
      if (filters?.read === true) {
        // Фильтруем на клиенте, чтобы избежать несовместимых перегрузок билдера
        return (data as DbNotification[]).filter((n: DbNotification) => n.read === true);
      }
      return data as DbNotification[];
    },
    enabled: !!user
  });

  // Запрос количества непрочитанных уведомлений
  const { data: unreadCount = 0 } = useQuery({
    queryKey: [TABLE_NAMES.NOTIFICATIONS, 'unread', user?.id],
    queryFn: async () => {
      if (!user) return 0;
      
      // Подсчёт непрочитанных: сумма (read=false) + (read IS NULL)
      // ВАЖНО: независимые билдеры для корректных условий
      const qFalse = supabaseWorker
        .from(TABLE_NAMES.NOTIFICATIONS)
        .select('id')
        .eq('user_id', user.id)
        .eq('read', false);

      const qNull = supabaseWorker
        .from(TABLE_NAMES.NOTIFICATIONS)
        .select('id')
        .eq('user_id', user.id)
        .is('read', null);

      const [{ data: dFalse, error: eFalse }, { data: dNull, error: eNull }] = await Promise.all([
        qFalse,
        qNull,
      ]);
      if (eFalse) throw eFalse;
      if (eNull) throw eNull;
      const listFalse = (dFalse || []) as { id: string }[];
      const listNull = (dNull || []) as { id: string }[];
      const ids = new Set<string>([...listFalse.map((r) => r.id), ...listNull.map((r) => r.id)]);
      return ids.size;
    },
    enabled: !!user
  });

  // Конвертация данных из БД в клиентский формат
  const notifications = useMemo(() => 
    dbNotifications.map(dbNotificationToNotification), 
    [dbNotifications]
  );

  // Мутация создания
  const createMutation = useMutation({
    mutationFn: async (data: NotificationCreate) => {
      if (!user) throw new Error('Пользователь не авторизован');
      
      const dbData = {
        ...notificationCreateToDb(data),
        user_id: user.id,
        read: false
      };
      
      const { data: result, error } = await supabaseWorker
        .from(TABLE_NAMES.NOTIFICATIONS)
        .insert(dbData)
        .select()
        .single();
      
      if (error) throw error;
      return result as DbNotification;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TABLE_NAMES.NOTIFICATIONS] });
    }
  });

  // Мутация обновления
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: NotificationUpdate }) => {
      const dbData = notificationUpdateToDb(data);
      
      const { data: result, error } = await supabaseWorker
        .from(TABLE_NAMES.NOTIFICATIONS)
        .update(dbData)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return result as DbNotification;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TABLE_NAMES.NOTIFICATIONS] });
    }
  });

  // Мутация удаления
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabaseWorker
        .from(TABLE_NAMES.NOTIFICATIONS)
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TABLE_NAMES.NOTIFICATIONS] });
    }
  });

  // Мутация отметки как прочитанное
  const markAsReadMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabaseWorker
        .from(TABLE_NAMES.NOTIFICATIONS)
        .update({ 
          read: true, 
          read_at: new Date().toISOString() 
        })
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TABLE_NAMES.NOTIFICATIONS] });
    }
  });

  // Мутация отметки всех как прочитанные
  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('Пользователь не авторизован');
      
      const { error } = await supabaseWorker
        .from(TABLE_NAMES.NOTIFICATIONS)
        .update({ 
          read: true, 
          read_at: new Date().toISOString() 
        })
        .eq('user_id', user.id)
        .eq('read', false);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TABLE_NAMES.NOTIFICATIONS] });
    }
  });

  // Обертки для операций с правильной типизацией
  const createNotification = async (data: NotificationCreate): Promise<Notification> => {
    const dbResult = await createMutation.mutateAsync(data);
    return dbNotificationToNotification(dbResult);
  };

  const updateNotification = async (id: string, data: NotificationUpdate): Promise<Notification> => {
    const dbResult = await updateMutation.mutateAsync({ id, data });
    return dbNotificationToNotification(dbResult);
  };

  const deleteNotification = async (id: string): Promise<void> => {
    await deleteMutation.mutateAsync(id);
  };

  const markAsRead = async (id: string): Promise<void> => {
    await markAsReadMutation.mutateAsync(id);
  };

  const markAllAsRead = async (): Promise<void> => {
    await markAllAsReadMutation.mutateAsync();
  };

  return {
    notifications,
    loading,
    error: error as Error | null,
    unreadCount,
    createNotification,
    updateNotification,
    deleteNotification,
    markAsRead,
    markAllAsRead,
    refetch
  };
};
